import type {ChatMessage,ThreadRecord} from '@reados/contracts';
import type {TranscriptForkAction} from '@reados/renderer';

export interface FinalReplyForkBoundary {sourceThreadId:string;lastTurnId:string}
const uuidV4=/^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

/** The owning Web conversation may contain original native IDs inherited by a
 * previous fork. Match the exact saved message; do not replace its turn with
 * the current conversation's latest turn or require its native thread ID. */
export function finalReplyForkBoundary(thread:ThreadRecord|null,messages:ChatMessage[],action:TranscriptForkAction):FinalReplyForkBoundary {
 const matches=messages.filter(message=>message.id===action.messageID&&message.threadId===action.threadId&&message.turnId===action.turnId);
 if(!thread?.codexThreadId||!action.turnId||!action.threadId||matches.length!==1||matches[0].role!=='assistant'||matches[0].status!=='complete')
  throw new Error('无法确认这条回复的分叉位置，请等待回复完成或刷新对话后重试。');
 return {sourceThreadId:thread.id,lastTurnId:action.turnId};
}

export interface FinalReplyForkOptions {
 boundary:FinalReplyForkBoundary;
 readKey:(identity:string)=>unknown;
 writeKey:(identity:string,key:string|null)=>void;
 create:(sourceThreadId:string,params:{lastTurnId:string;idempotencyKey:string;forkKind:'fork'})=>Promise<ThreadRecord>;
 open:(thread:ThreadRecord)=>Promise<void>;
}

/** One explicit click is one creation attempt. A lost HTTP response or failed
 * navigation retains its key; successful navigation permits a later new fork. */
export function createFinalReplyForkOperation(){
 const attempts=new Map<string,{key:string;thread?:ThreadRecord}>();
 let flight:Promise<void>|null=null;
 return {
  get busy(){return flight!==null;},
  run(options:FinalReplyForkOptions):Promise<void>{
   if(flight)return flight;
   const {boundary,readKey,writeKey,create,open}=options;
   const identity=JSON.stringify([boundary.sourceThreadId,boundary.lastTurnId]);
   let attempt=attempts.get(identity);
   if(!attempt){const saved=readKey(identity);attempt={key:typeof saved==='string'&&uuidV4.test(saved)?saved:crypto.randomUUID()};attempts.set(identity,attempt);}
   const retained=attempt;
   writeKey(identity,retained.key);
   flight=Promise.resolve().then(async()=>{
    if(!retained.thread){
     const created=await create(boundary.sourceThreadId,{lastTurnId:boundary.lastTurnId,idempotencyKey:retained.key,forkKind:'fork'});
     if(!created?.id||created.id===boundary.sourceThreadId||created.forkedFromThreadId!==boundary.sourceThreadId)
      throw new Error('服务端尚未确认新的分叉对话，请重试。');
     retained.thread=created;
    }
    await open(retained.thread);
    attempts.delete(identity);writeKey(identity,null);
   }).finally(()=>{flight=null;});
   return flight;
  },
 };
}
